import { useLazyQuery } from '@apollo/client';
import { css } from '@emotion/core';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    IconButton,
    TextField,
    Tooltip,
    Typography
} from '@material-ui/core';
import { ExpandMore, Search } from '@material-ui/icons';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import validator from 'validator';

import { formatEmail } from 'shared/common/email-utils';

import { formatNumber as formatPhoneNumber, isValidPhoneNumber } from '../common/phone';
import { fullDateTime } from '../common/timestamp';
import { PersonSearchResult, SEARCH_PERSON } from '../graphql/queries/person';
import { useReduxState } from '../hooks/use-redux';
import { useSession } from '../hooks/use-session';
import { Avatar } from '../sfc/avatar';
import { JobStatus } from '../state';

const enterKey = 'Enter';
const loadingSize = 32;
const loadingThickness = 2;
interface SearchBoxProps {
    onSelect?: (selected: Array<{ personId: string; jobId: string }>) => void;
    prioritizeJobId?: string;
}

const styles = css`
    .MuiDialogTitle-root {
        min-width: 640px;
        padding: 8px 16px;
    }

    .search-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px;
        gap: 10px;

        > div {
            display: flex;
            align-items: center;
        }

        .counts {
            font-size: 14px;
            flex: 0 0 auto;
        }

        .search-icon {
            flex: 0 0 auto;
        }

        .search-input {
            flex: 1 1 auto;
        }
    }

    .MuiDialogContent-root {
        :first-child {
            padding-top: 8px;
        }
        padding: 8px;
    }

    .results-container {
        .panel-summary {
            display: flex;
            align-items: center;
            gap: 15px;
        }

        .MuiExpansionPanel-root {
            border: none;
            box-shadow: none;
            border-bottom: thin solid #e0e0e0;
            border-radius: 0;

            &:last-child {
                border-bottom: none;
            }

            &.Mui-expanded {
                margin: 0;
            }

            &::before {
                display: none;
            }
        }

        .MuiExpansionPanelSummary-content.Mui-expanded {
            margin: 0;
        }

        .MuiExpansionPanelDetails-root {
            padding-left: 72px;
            font-size: 14px;

            .job-item {
                margin-bottom: 10px;
            }
        }
    }
`;

export const SearchBox: React.FC<SearchBoxProps> = ({ onSelect, prioritizeJobId }) => {
    const { user } = useSession();
    const jobStages = useReduxState((state) => state.appConstants.constants.jobStages);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selected, setSelected] = useState<Array<{ personId: string; jobId: string }>>(onSelect ? [] : null);
    const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

    const [executeSearch, { data, loading: searching }] = useLazyQuery(SEARCH_PERSON, {
        fetchPolicy: 'network-only'
    });

    const [cachedResults, setCachedResults] = useState(null);
    const searchResults = cachedResults;
    const sessionUserId = user.id;

    // Update cached results when Apollo data changes
    useEffect(() => {
        if (data?.searchPerson) {
            setCachedResults(data.searchPerson);
        }
    }, [data, searching]);

    useEffect(() => {
        if (searchResults) {
            setExpandedPanels(searchResults.map((r: any) => r.id));
        }
    }, [searchResults]);

    const handleSearchIconTap = useCallback(() => {
        setDialogOpen(true);
    }, []);

    const handleDialogClose = useCallback(() => {
        setDialogOpen(false);
        setSearchText('');
        setCachedResults(null);
    }, []);

    const handleTextChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
        setCachedResults(null);
    }, []);

    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === enterKey && searchText.trim()) {
                // Client-side preprocessing
                const trimmed = searchText.trim();
                const isEmail = validator.isEmail(trimmed);
                const isPhone = isValidPhoneNumber(trimmed);

                let personName = null;
                let email = null;
                let phone = null;

                if (isEmail) {
                    email = formatEmail(trimmed);
                } else if (isPhone) {
                    phone = formatPhoneNumber(trimmed);
                } else {
                    // Name search - convert to wildcard pattern
                    personName = `%${_.toLower(trimmed).split(/\s+/).join('%')}%`;
                }

                executeSearch({
                    variables: {
                        email,
                        personName,
                        phone
                    }
                });
            }
        },
        [executeSearch, searchText]
    );

    const handleSelect = useCallback(
        (val: { personId: string; jobId: string }) => (event: { target: { checked: boolean } }) => {
            if (event.target.checked) {
                setSelected((prev) => prev.concat(val));
            } else {
                setSelected((prev) => prev.filter((s) => s.jobId !== val.jobId && s.personId !== val.personId));
            }
        },
        []
    );

    const handleConfirm = useCallback(() => {
        handleDialogClose();
        onSelect(selected);
    }, [handleDialogClose, onSelect, selected]);

    const handlePanelToggle = useCallback(
        (personId: string) => () => {
            setExpandedPanels((prev) => {
                const isExpanded = prev.includes(personId);
                return isExpanded ? prev.filter((id) => id !== personId) : [...prev, personId];
            });
        },
        []
    );

    const handlePersonNameClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    const getSortedSearchResults = useCallback((): PersonSearchResult[] | null => {
        if (!searchResults) return null;

        if (!prioritizeJobId) {
            return searchResults;
        }

        const hasCandidateInJob = searchResults.filter((r: PersonSearchResult) =>
            r.jobs.find((j: PersonSearchResult['jobs'][0]) => j.id === prioritizeJobId)
        );
        const other = _.differenceBy(searchResults, hasCandidateInJob, 'id');
        return hasCandidateInJob.concat(other);
    }, [searchResults, prioritizeJobId]);

    const sortedSearchResults = getSortedSearchResults();

    const icon = (
        <Tooltip title="Search for Candidate">
            <IconButton onClick={handleSearchIconTap}>
                <Search style={{ color: '#ffffff' }} />
            </IconButton>
        </Tooltip>
    );

    let results;
    let spinner;
    let count;

    if (searching) {
        spinner = (
            <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 120 }}>
                <CircularProgress size={loadingSize} thickness={loadingThickness} />
            </div>
        );
    } else if (sortedSearchResults) {
        const countText = `${sortedSearchResults.length >= 100 ? '100+' : sortedSearchResults.length} results`;
        count = <div className="counts">{countText}</div>;

        // Use sortBy (stable sort) to preserve SQL similarity ordering
        // Only bump user's assigned people to the top
        const rows = _.sortBy(sortedSearchResults, [
            (record) => (record.jobs.some((j) => j.assignee === sessionUserId) ? 0 : 1)
        ]).map((record) => {
            const hasJobs = record.jobs.length > 0;
            const isExpanded = expandedPanels.includes(record.id);
            const jobCount = record.jobs.length;

            const jobs = _.orderBy(
                record.jobs,
                [
                    (j) => (j.assignee === sessionUserId ? 1 : 0),
                    (j) => j.lastStageChangedAt,
                    (j) => (j.disqualified ? 0 : 1)
                ],
                ['desc', 'desc', 'desc']
            ).map((j) => {
                const stage = jobStages.find((s) => s.name === j.stage);
                const stageLabel = stage ? stage.label : j.stage;
                const jobDisqualified = j.disqualified || j.jobStatus !== JobStatus.Active;

                const body = (
                    <div className={`job-item ${jobDisqualified ? 'disqualified' : ''}`}>
                        <div className="job-title">
                            <span className="list-centered-icon flex-wrap">
                                <span>{j.client}</span>
                                <i className="material-icons separator">chevron_right</i>
                                {j.title}
                            </span>
                        </div>
                        <div className="stage">
                            {stageLabel} - <span className="secondary">{fullDateTime(j.lastStageChangedAt)}</span>
                        </div>
                    </div>
                );

                if (!onSelect) {
                    return (
                        <Link key={j.id} target="_blank" to={`/job/${j.id}/board/candidate/${record.id}`}>
                            {body}
                        </Link>
                    );
                }

                return (
                    <div className="job-detail-with-checkbox" key={j.id}>
                        {body}
                        <Checkbox color="primary" onChange={handleSelect({ jobId: j.id, personId: record.id })} />
                    </div>
                );
            });

            const jobsLabel = `${jobCount} ${jobCount === 1 ? 'job' : 'jobs'}`;

            const summaryContent = (
                <div className="panel-summary">
                    <Avatar entity={record} />
                    <Link
                        className="person-name-link"
                        onClick={handlePersonNameClick}
                        target="_blank"
                        to={`/person/${record.id}`}
                    >
                        <Typography variant="h6">{record.name.full}</Typography>
                        <Typography color="textSecondary" variant="subtitle2">
                            {jobsLabel}
                        </Typography>
                    </Link>
                </div>
            );

            const expandIcon = hasJobs ? <ExpandMore /> : null;
            const expansionPanelDetails = hasJobs ? (
                <ExpansionPanelDetails style={{ flexDirection: 'column', paddingTop: 0 }}>{jobs}</ExpansionPanelDetails>
            ) : null;

            return (
                <ExpansionPanel
                    expanded={hasJobs && isExpanded}
                    key={record.id}
                    onChange={hasJobs ? handlePanelToggle(record.id) : undefined}
                >
                    <ExpansionPanelSummary expandIcon={expandIcon}>{summaryContent}</ExpansionPanelSummary>
                    {expansionPanelDetails}
                </ExpansionPanel>
            );
        });

        results = <div className="results-container">{rows}</div>;
    }

    const actions =
        selected && sortedSearchResults?.length > 0 ? (
            <DialogActions>
                <Button color="primary" disabled={selected.length === 0} onClick={handleConfirm}>
                    Confirm
                </Button>
            </DialogActions>
        ) : null;

    const dialogContent =
        results || spinner ? (
            <DialogContent>
                {results}
                {spinner}
            </DialogContent>
        ) : null;

    const dialog = (
        <Dialog maxWidth="md" onClose={handleDialogClose} open={dialogOpen} css={styles}>
            <DialogTitle>
                <div className="search-header">
                    <div className="search-icon">
                        <Search />
                    </div>
                    <div className="search-input">
                        <TextField
                            InputProps={{ disableUnderline: true }}
                            autoFocus={true}
                            fullWidth={true}
                            disabled={searching}
                            onChange={handleTextChange}
                            onKeyPress={handleKeyPress}
                            placeholder="Search"
                            value={searchText}
                        />
                    </div>
                    {count}
                </div>
            </DialogTitle>
            {dialogContent}
            {actions}
        </Dialog>
    );

    return (
        <div>
            {icon}
            {dialog}
        </div>
    );
};
